/**
 * Wires `feature:single_row_toolbar_toggle` analytics for the row-details
 * toolbar. Returns onChange handlers that flip the underlying useState
 * setters AND fire the matching event:
 *   - hideNulls / group → `{ enabled: boolean }`
 *   - density           → `{ mode: 'compact' | 'normal' }`
 *   - search            → debounced `{ enabled: boolean }` (one event per
 *                          on/off transition; ignores intermediate keystrokes)
 *
 * Kept in its own hook so `RowDetailsPanel.tsx` stays under the 200-line
 * budget. Pure renderer-side — no IPC, no PII.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';

const SEARCH_ANALYTICS_DEBOUNCE_MS = 400;

export type ToolbarAnalyticsHandlers = {
  readonly onToggleHideNulls: () => void;
  readonly onToggleDensity: () => void;
  readonly onToggleGroupByCategory: () => void;
  readonly onCellSearchChange: (value: string) => void;
};

export type UseToolbarAnalyticsParams = {
  readonly setHideNulls: (updater: (prev: boolean) => boolean) => void;
  readonly setDensity: (updater: (prev: 'compact' | 'normal') => 'compact' | 'normal') => void;
  readonly setGroupByCategory: (updater: (prev: boolean) => boolean) => void;
  readonly setCellSearch: (value: string) => void;
};

export function useToolbarAnalytics(
  params: UseToolbarAnalyticsParams,
): ToolbarAnalyticsHandlers {
  const { setHideNulls, setDensity, setGroupByCategory, setCellSearch } = params;
  const { track } = useAnalytics();

  const onToggleHideNulls = useCallback(() => {
    setHideNulls((prev) => {
      const next = !prev;
      track('feature:single_row_toolbar_toggle', { setting: 'hideNulls', enabled: next });
      return next;
    });
  }, [track, setHideNulls]);

  const onToggleDensity = useCallback(() => {
    setDensity((prev) => {
      const next: 'compact' | 'normal' = prev === 'normal' ? 'compact' : 'normal';
      track('feature:single_row_toolbar_toggle', { setting: 'density', mode: next });
      return next;
    });
  }, [track, setDensity]);

  const onToggleGroupByCategory = useCallback(() => {
    setGroupByCategory((prev) => {
      const next = !prev;
      track('feature:single_row_toolbar_toggle', { setting: 'group', enabled: next });
      return next;
    });
  }, [track, setGroupByCategory]);

  // Debounce the search analytics event — fire once after the user stops
  // typing, with `enabled = query.length > 0`. Suppress runs where the
  // on/off bit didn't actually change.
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEnabled = useRef<boolean | null>(null);
  const onCellSearchChange = useCallback(
    (value: string) => {
      setCellSearch(value);
      if (searchTimer.current) clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => {
        const enabled = value.trim().length > 0;
        if (lastEnabled.current === enabled) return;
        lastEnabled.current = enabled;
        track('feature:single_row_toolbar_toggle', { setting: 'search', enabled });
      }, SEARCH_ANALYTICS_DEBOUNCE_MS);
    },
    [track, setCellSearch],
  );

  // Empty deps intentional — the ref is stable; adding it to deps would re-run cleanup
  // each render and clear the debounce timer before its 400ms timeout fires.
  useEffect(() => {
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, []);

  return { onToggleHideNulls, onToggleDensity, onToggleGroupByCategory, onCellSearchChange };
}
